// src/pages/products/ProductsPage.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../api/axiosConfig';
import Register from '../../components/user/Register';

// Kiểu dữ liệu
interface Product {
  slug: string;
  name: string;
  description: string;
  mainImage: string | null;
  shortFeatures: string[];
  order?: number;
}

export default function ProductsPage() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Lấy baseURL thực tế của axios instance (để debug & xử lý ảnh)
  const apiBaseURL = api.defaults.baseURL || '';
  // Bản không có /api để ghép ảnh tĩnh (VD: /uploads/xxx.jpg)
  const apiBaseWithoutApi = apiBaseURL.replace(/\/api\/?$/, '');

  useEffect(() => {
    document.title = t('products_page.page_title');

    const fetchProducts = async () => {
      try {
        setLoading(true);

        // ---- DEBUG: in ra baseURL để bạn kiểm chứng trên DevTools ----
        console.log('[ProductsPage] api.baseURL =', apiBaseURL);

        // CHÚ Ý: endpoint chỉ là "/products", không thêm "/api"
        const endpoint = '/products';
        const { data } = await api.get(endpoint, {
          params: { lang: currentLang },
        });

        // Chuẩn hoá dữ liệu
        const mapped: Product[] = (data || []).map((p: any) => ({
          slug: p.slug,
          name: currentLang === 'vi' ? p.name_vi || p.name : p.name_en || p.name,
          description:
            currentLang === 'vi'
              ? p.description_vi || p.description || ''
              : p.description_en || p.description || '',
          mainImage: p.mainImage || null,
          shortFeatures: currentLang === 'vi'
              ? p.short_features_vi || []
              : p.short_features_en || [],
          order: typeof p.order === 'number' ? p.order : 0,
        }));

        setProducts(
          mapped.sort((a, b) => {
            const diff = (a.order ?? 0) - (b.order ?? 0);
            return diff !== 0 ? diff : a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' });
          })
        );
      } catch (error) {
        console.error('❌ Lỗi khi tải danh sách sản phẩm:', error);
        setProducts([]); // tránh render cũ
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [currentLang, t, apiBaseURL]);

  // Resolve URL ảnh an toàn (hỗ trợ cả absolute và relative)
  const resolveImageUrl = (path: string) => {
    if (!path) return '/placeholder.jpg';

    // ✅ Nếu là URL Cloudinary hoặc URL tuyệt đối → giữ nguyên
    if (/^https?:\/\//i.test(path) || path.includes("res.cloudinary.com")) return path;

    // ✅ Nếu là ảnh trong thư mục uploads local (chạy dev) → nối baseURL
    const base = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
    return `${base}${path.startsWith('/') ? path : '/' + path}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen font-semibold text-lg">
        Đang tải sản phẩm...
      </div>
    );
  }

  return (
    <>
      <main className="bg-gray-50">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div
                key={product.slug}
                className="bg-white overflow-hidden flex flex-col group transition-transform duration-300 hover:-translate-y-2 rounded-lg shadow"
              >
                {/* Ảnh chính */}
                <Link
                  to={`/${currentLang}/products/${product.slug}`}
                  className="block h-56 bg-gray-100 p-4"
                >
                  {product.mainImage ? (
                    <img
                      src={resolveImageUrl(product.mainImage) || ''}
                      alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300 ease-in-out"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No Image
                    </div>
                  )}
                </Link>

                {/* Nội dung */}
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold text-gray-900">
                    <Link
                      to={`/${currentLang}/products/${product.slug}`}
                      className="hover:text-green-700 transition-colors"
                    >
                      {product.name}
                    </Link>
                  </h3>

                  {/* short_features */}
                  {product.shortFeatures.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {product.shortFeatures.map((f, idx) => (
                        <span
                          key={idx}
                          className="text-sm font-semibold bg-[#DBF8C8] text-green-800 px-3 py-1 rounded"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Mô tả */}
                  <p className="text-gray-600 mt-3 text-sm">
                    {product.description || t('products_page.no_description')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Register />
    </>
  );
}
